/**
 * Фирменный облик стрелок и заметок ANZONIX.
 *
 * Стрелка — не палка с треугольником, а след: тонкий прозрачный хвост
 * набирает цвет и толщину к голове, голова с вырезом, по центру светлая
 * жилка, вокруг мягкое свечение. Изогнутую выгибают третьей опорой.
 * Заметка — стеклянная карточка-пузырь с хвостиком к своей точке; сноска
 * тянется к карточке плавной кривой; метка цены — ярлык; флаг — полотнище
 * с переливом. Всё — одним языком: скриншот узнаётся по разметке.
 */

import { parseColor, withAlpha } from './color.js';
import type { Drawing } from './types.js';
import type { DrawScope, Rect, Screen } from './shapes.js';
import { textStyleOf, wrapText, type TextStyle } from './text-style.js';

export const LOOK_KINDS = new Set<string>(['arrow', 'arrow-curve', 'arrow-up', 'arrow-down', 'note', 'callout', 'price-label', 'flag']);

function rgb(color: string): [number, number, number] | null {
  const c = parseColor(color);
  return c ? [c.r, c.g, c.b] : null;
}

/** Подкрашивание: плотность умножается на собственную плотность цвета. */
function tint(color: string, alpha: number): string {
  return withAlpha(color, alpha);
}

/** Светлее цвета — для жилки и бликов. */
function lighter(color: string, amount: number, alpha = 1): string {
  const c = parseColor(color);
  if (!c) return color;
  const mix = (v: number): number => Math.round(v + (255 - v) * amount);
  return `rgba(${mix(c.r)}, ${mix(c.g)}, ${mix(c.b)}, ${+Math.min(1, alpha * c.a).toFixed(3)})`;
}

/* ---------------------------------------------------------------- стрелки */

/** Точки кривой от начала к концу через изгиб; без изгиба — прямая. */
function curvePoints(from: Screen, to: Screen, bend: Screen | null, steps = 28): Screen[] {
  const out: Screen[] = [];
  for (let i = 0; i <= steps; i += 1) {
    const t = i / steps;
    if (!bend) {
      out.push({ x: from.x + (to.x - from.x) * t, y: from.y + (to.y - from.y) * t });
      continue;
    }
    const u = 1 - t;
    out.push({
      x: u * u * from.x + 2 * u * t * bend.x + t * t * to.x,
      y: u * u * from.y + 2 * u * t * bend.y + t * t * to.y,
    });
  }
  return out;
}

/** Темнее цвета — для теневой грани наконечника. */
function darker(color: string, amount: number, alpha = 1): string {
  const c = rgb(color);
  if (!c) return color;
  const mix = (v: number): number => Math.round(v * (1 - amount));
  return `rgba(${mix(c[0])}, ${mix(c[1])}, ${mix(c[2])}, ${alpha})`;
}

/** Точки кривой с шагом по длине: даже при сильном приближении их хватает. */
function densePath(from: Screen, to: Screen, bend: Screen | null): Screen[] {
  const rough = Math.hypot(to.x - from.x, to.y - from.y) + (bend ? Math.hypot(bend.x - from.x, bend.y - from.y) : 0);
  const steps = Math.max(16, Math.min(240, Math.round(rough / 6)));
  return curvePoints(from, to, bend, steps);
}

/**
 * Основание наконечника — точка на пути ровно за `back` пикселей до острия.
 *
 * Раньше основание искалось «по точкам пути»: при сильном приближении
 * отрезок между соседними точками выходил длиннее головы, основание
 * совпадало с остриём, и голова схлопывалась — стрелка оставалась без
 * наконечника. Теперь точка берётся с долей внутри отрезка.
 */
export function cutBefore(path: readonly Screen[], back: number): { body: Screen[]; base: Screen } {
  let travelled = 0;
  for (let i = path.length - 1; i > 0; i -= 1) {
    const a = path[i]!;
    const b = path[i - 1]!;
    const segment = Math.hypot(a.x - b.x, a.y - b.y);
    if (travelled + segment >= back) {
      const share = segment === 0 ? 0 : (back - travelled) / segment;
      const base = { x: a.x + (b.x - a.x) * share, y: a.y + (b.y - a.y) * share };
      return { body: [...path.slice(0, i), base], base };
    }
    travelled += segment;
  }
  const first = path[0]!;
  return { body: [first, first], base: first };
}

/** Детерминированный «шум»: искры не должны дрожать от кадра к кадру. */
function hash(n: number): number {
  const x = Math.sin(n * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
}

/**
 * Стрелка-призма — фирменная стрелка ANZONIX.
 *
 * Не линия с треугольником, а световой след:
 * — шлейф сужается к хвосту и тает в прозрачность, как след кометы;
 * — у шлейфа объём: светлая верхняя грань и светящаяся сердцевина;
 * — наконечник огранён в две грани — светлую и теневую — с бликом по
 *   кромке, как кристалл, и лёгким вырезом у основания;
 * — вдоль шлейфа летят искры, у старта — светящаяся точка.
 * Всё в цвете фигуры: стрелка любого цвета остаётся узнаваемой.
 */
export function cometArrow(ctx: CanvasRenderingContext2D, from: Screen, to: Screen, bend: Screen | null, width: number, color: string): void {
  const total = Math.hypot(to.x - from.x, to.y - from.y);
  if (total < 4) return;
  const shaft = 3.4 + width * 2.2;
  const headWidth = Math.max(16, shaft * 3.2);
  const headLength = Math.min(total * 0.45, headWidth * 1.25);

  const path = densePath(from, to, bend);
  const { body, base } = cutBefore(path, headLength * 0.78);
  const dx = to.x - base.x;
  const dy = to.y - base.y;
  const dl = Math.hypot(dx, dy) || 1;
  const ux = dx / dl;
  const uy = dy / dl;
  const nx = -uy;
  const ny = ux;

  // Контур шлейфа: от иглы у хвоста до полной толщины у головы.
  const left: Screen[] = [];
  const right: Screen[] = [];
  const last = body.length - 1 || 1;
  body.forEach((point, i) => {
    const next = body[Math.min(i + 1, body.length - 1)]!;
    const prev = body[Math.max(i - 1, 0)]!;
    const tx = next.x - prev.x;
    const ty = next.y - prev.y;
    const tl = Math.hypot(tx, ty) || 1;
    const px = -ty / tl;
    const py = tx / tl;
    const half = (shaft / 2) * (0.12 + 0.88 * Math.pow(i / last, 0.7));
    left.push({ x: point.x + px * half, y: point.y + py * half });
    right.push({ x: point.x - px * half, y: point.y - py * half });
  });

  const ribbon = (): void => {
    ctx.beginPath();
    ctx.moveTo(left[0]!.x, left[0]!.y);
    for (const q of left.slice(1)) ctx.lineTo(q.x, q.y);
    for (let i = right.length - 1; i >= 0; i -= 1) ctx.lineTo(right[i]!.x, right[i]!.y);
    ctx.closePath();
  };

  ctx.save();
  ctx.setLineDash([]);
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';

  // 1. Шлейф со свечением: тает к хвосту, наливается к голове.
  const trail = ctx.createLinearGradient(from.x, from.y, base.x, base.y);
  trail.addColorStop(0, tint(color, 0));
  trail.addColorStop(0.35, tint(color, 0.32));
  trail.addColorStop(0.8, tint(color, 0.85));
  trail.addColorStop(1, lighter(color, 0.12, 1));
  ctx.shadowColor = tint(color, 0.65);
  ctx.shadowBlur = 14 + width * 3;
  ribbon();
  ctx.fillStyle = trail;
  ctx.fill();
  ctx.shadowBlur = 0;

  // 2. Верхняя грань шлейфа светлее — объём, как у стеклянной трубки.
  ctx.beginPath();
  ctx.moveTo(left[0]!.x, left[0]!.y);
  for (const q of left.slice(1)) ctx.lineTo(q.x, q.y);
  for (let i = body.length - 1; i >= 0; i -= 1) ctx.lineTo(body[i]!.x, body[i]!.y);
  ctx.closePath();
  const bevel = ctx.createLinearGradient(from.x, from.y, base.x, base.y);
  bevel.addColorStop(0, lighter(color, 0.6, 0));
  bevel.addColorStop(1, lighter(color, 0.6, 0.35));
  ctx.fillStyle = bevel;
  ctx.fill();

  // 3. Сердцевина — светящаяся нить по центру шлейфа.
  const coreFrom = body[Math.floor(body.length * 0.3)] ?? body[0]!;
  const core = ctx.createLinearGradient(coreFrom.x, coreFrom.y, base.x, base.y);
  core.addColorStop(0, lighter(color, 0.85, 0));
  core.addColorStop(1, lighter(color, 0.85, 0.9));
  ctx.beginPath();
  const coreStart = Math.floor(body.length * 0.3);
  ctx.moveTo(body[coreStart]!.x, body[coreStart]!.y);
  for (const q of body.slice(coreStart + 1)) ctx.lineTo(q.x, q.y);
  ctx.strokeStyle = core;
  ctx.lineWidth = Math.max(0.9, shaft * 0.17);
  ctx.stroke();

  // 4. Искры вдоль шлейфа: мельче и бледнее к хвосту.
  for (let k = 0; k < 7; k += 1) {
    const t = 0.08 + (k / 7) * 0.5 + hash(k) * 0.04;
    const at = body[Math.min(body.length - 1, Math.round(t * last))]!;
    const side = k % 2 === 0 ? 1 : -1;
    const offset = shaft * (0.9 + hash(k + 11) * 1.4) * side;
    const radius = 0.6 + t * 1.8;
    ctx.beginPath();
    ctx.arc(at.x + nx * offset, at.y + ny * offset, radius, 0, Math.PI * 2);
    ctx.fillStyle = lighter(color, 0.55, 0.15 + t * 0.6);
    ctx.fill();
  }

  // 5. Наконечник-призма: силуэт со свечением, потом две грани.
  const notch = headLength * 0.24;
  const wingL = { x: base.x + nx * (headWidth / 2) - ux * notch, y: base.y + ny * (headWidth / 2) - uy * notch };
  const wingR = { x: base.x - nx * (headWidth / 2) - ux * notch, y: base.y - ny * (headWidth / 2) - uy * notch };
  const inset = { x: base.x + ux * notch * 0.35, y: base.y + uy * notch * 0.35 };

  ctx.shadowColor = tint(color, 0.8);
  ctx.shadowBlur = 16 + width * 3;
  ctx.beginPath();
  ctx.moveTo(to.x, to.y);
  ctx.lineTo(wingL.x, wingL.y);
  ctx.lineTo(inset.x, inset.y);
  ctx.lineTo(wingR.x, wingR.y);
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
  ctx.shadowBlur = 0;

  // Светлая грань — к свету.
  ctx.beginPath();
  ctx.moveTo(to.x, to.y);
  ctx.lineTo(wingL.x, wingL.y);
  ctx.lineTo(inset.x, inset.y);
  ctx.closePath();
  const lit = ctx.createLinearGradient(wingL.x, wingL.y, to.x, to.y);
  lit.addColorStop(0, lighter(color, 0.25, 1));
  lit.addColorStop(1, lighter(color, 0.6, 1));
  ctx.fillStyle = lit;
  ctx.fill();

  // Теневая грань — от света.
  ctx.beginPath();
  ctx.moveTo(to.x, to.y);
  ctx.lineTo(inset.x, inset.y);
  ctx.lineTo(wingR.x, wingR.y);
  ctx.closePath();
  ctx.fillStyle = darker(color, 0.28, 1);
  ctx.fill();

  // Блик по кромке светлой грани и тонкий контур — кристалл, а не заливка.
  ctx.beginPath();
  ctx.moveTo(wingL.x, wingL.y);
  ctx.lineTo(to.x, to.y);
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.7)';
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(to.x, to.y);
  ctx.lineTo(wingL.x, wingL.y);
  ctx.lineTo(inset.x, inset.y);
  ctx.lineTo(wingR.x, wingR.y);
  ctx.closePath();
  ctx.strokeStyle = lighter(color, 0.5, 0.55);
  ctx.lineWidth = 0.8;
  ctx.stroke();

  // 6. Старт — светящаяся точка с ореолом: откуда пошло движение.
  const dot = 2 + width * 0.45;
  ctx.beginPath();
  ctx.arc(from.x, from.y, dot + 4, 0, Math.PI * 2);
  ctx.fillStyle = tint(color, 0.14);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(from.x, from.y, dot, 0, Math.PI * 2);
  ctx.fillStyle = lighter(color, 0.45, 0.9);
  ctx.fill();
  ctx.restore();
}

/* ------------------------------------------------------------- подписи */

interface Card {
  readonly rect: Rect;
  readonly lines: readonly string[];
  readonly lineHeight: number;
  readonly padX: number;
}

function measureCard(ctx: CanvasRenderingContext2D, text: string, font: string, at: Screen, style: TextStyle): Card {
  ctx.save();
  ctx.font = font;
  // Перенос строк — по настройке, как у голой надписи.
  const lines = wrapText(text || ' ', style, (line) => ctx.measureText(line).width);
  const lineHeight = Math.round(style.fontSize * 1.5);
  const padX = 16;
  const width = Math.max(56, ...lines.map((line) => ctx.measureText(line).width)) + padX + 13;
  const height = lines.length * lineHeight + 16;
  ctx.restore();
  /*
   * Карточка стоит там, где её поставили, и уезжает с графиком за край,
   * как всё остальное. Раньше у края её удерживали внутри холста — и
   * при прокрутке заметки прилипали к краю экрана, отрываясь от места
   * на графике, к которому относятся.
   */
  const left = at.x;
  const top = at.y;
  return { rect: { left, top, right: left + width, bottom: top + height }, lines, lineHeight, padX };
}

/** Светлый ли цвет: на светлой подложке текст должен быть тёмным. */
function isLight(color: string): boolean {
  const c = rgb(color);
  if (!c) return false;
  return (0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2]) / 255 > 0.62;
}

/** Цвет подложки карточки: свой — по настройке «Фон», иначе фирменное стекло. */
function cardFill(ctx: CanvasRenderingContext2D, rect: Rect, style: TextStyle): string | CanvasGradient {
  if (style.background) return tint(style.backgroundColor, style.backgroundOpacity);
  const glass = ctx.createLinearGradient(rect.left, rect.top, rect.left, rect.bottom);
  glass.addColorStop(0, 'rgba(24, 31, 56, 0.96)');
  glass.addColorStop(1, 'rgba(12, 16, 31, 0.96)');
  return glass;
}

/**
 * Карточка ANZONIX — поверхность заметки, сноски и подписей.
 *
 * Как в дорогом приложении: глубина — мягкой тенью, а не свечением;
 * кромка — волосяной линией, выровненной по пикселю; сверху тонкий
 * блик стекла; слева полоса акцента. Все настройки честные: свой фон и
 * его плотность, граница, кегль, начертание и перенос строк.
 */
function glassCard(ctx: CanvasRenderingContext2D, card: Card, color: string, style: TextStyle, font: string, placeholder = ''): void {
  const { rect } = card;
  const x = Math.round(rect.left) + 0.5;
  const y = Math.round(rect.top) + 0.5;
  const w = Math.round(rect.right - rect.left);
  const h = Math.round(rect.bottom - rect.top);
  const radius = 11;
  ctx.save();
  ctx.setLineDash([]);

  // Глубина: мягкая тень снизу.
  ctx.shadowColor = 'rgba(0, 0, 0, 0.55)';
  ctx.shadowBlur = 20;
  ctx.shadowOffsetY = 6;
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, radius);
  ctx.fillStyle = cardFill(ctx, rect, style);
  ctx.fill();
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;

  // Кромка: цвет фигуры, тающий слева направо; без границы — едва заметная.
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, radius);
  if (style.border) {
    const edge = ctx.createLinearGradient(x, y, x + w, y + h);
    edge.addColorStop(0, tint(color, 0.85));
    edge.addColorStop(1, tint(color, 0.25));
    ctx.strokeStyle = edge;
  } else {
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
  }
  ctx.lineWidth = 1;
  ctx.stroke();

  // Блик стекла по верхней кромке.
  ctx.beginPath();
  ctx.moveTo(x + radius, y + 1);
  ctx.lineTo(x + w - radius, y + 1);
  const sheen = ctx.createLinearGradient(x, y, x + w, y);
  sheen.addColorStop(0, 'rgba(255, 255, 255, 0)');
  sheen.addColorStop(0.3, 'rgba(255, 255, 255, 0.12)');
  sheen.addColorStop(1, 'rgba(255, 255, 255, 0)');
  ctx.strokeStyle = sheen;
  ctx.stroke();

  // Полоса акцента слева.
  ctx.beginPath();
  ctx.roundRect(x + 6, y + 8, 2.5, h - 16, 1.25);
  ctx.fillStyle = color;
  ctx.fill();

  // Текст: на своём светлом фоне — тёмный, иначе светлый с оттенком акцента.
  ctx.font = font;
  ctx.textBaseline = 'middle';
  const lightBackground = style.background && isLight(style.backgroundColor) && style.backgroundOpacity > 0.5;
  const empty = placeholder && card.lines.every((line) => line.trim() === '');
  ctx.fillStyle = lightBackground ? '#0B1020' : empty ? 'rgba(201, 212, 229, 0.4)' : lighter(color, 0.82, 1);
  (empty ? [placeholder] : card.lines).forEach((line, i) => {
    ctx.fillText(line, x + card.padX, y + 8 + card.lineHeight * (i + 0.5));
  });
  ctx.restore();
}

/** Булавка в точке привязки: тёмное кольцо, ядро цвета фигуры, ореол. */
function pin(ctx: CanvasRenderingContext2D, at: Screen, color: string): void {
  ctx.save();
  ctx.setLineDash([]);
  ctx.beginPath();
  ctx.arc(at.x, at.y, 7, 0, Math.PI * 2);
  ctx.fillStyle = tint(color, 0.16);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(at.x, at.y, 4.2, 0, Math.PI * 2);
  ctx.fillStyle = '#070B16';
  ctx.fill();
  ctx.beginPath();
  ctx.arc(at.x, at.y, 2.8, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();
  ctx.restore();
}

function ring(ctx: CanvasRenderingContext2D, at: Screen, color: string, radius = 3.5): void {
  ctx.save();
  ctx.setLineDash([]);
  ctx.beginPath();
  ctx.arc(at.x, at.y, radius + 3, 0, Math.PI * 2);
  ctx.fillStyle = tint(color, 0.18);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(at.x, at.y, radius, 0, Math.PI * 2);
  ctx.fillStyle = '#070B16';
  ctx.fill();
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.6;
  ctx.stroke();
  ctx.restore();
}

function priceWords(value: number | undefined): string {
  if (value === undefined || !Number.isFinite(value)) return '—';
  const abs = Math.abs(value);
  return value.toLocaleString('ru-RU', { maximumFractionDigits: abs >= 1000 ? 1 : abs >= 1 ? 2 : 6 });
}

/* ---------------------------------------------------------- отрисовка */

export function drawLook(scope: DrawScope, shape: Drawing, points: readonly (Screen | null)[]): void {
  const { ctx } = scope;
  const [a, b, c] = points;
  if (!a) return;
  const color = shape.color;
  const style = textStyleOf(shape.textStyle, shape.kind);
  // Карточкам — чуть плотнее начертание: так текст на стекле чётче.
  const font = `${style.italic ? 'italic ' : ''}${style.bold ? 700 : 500} ${style.fontSize}px Onest, system-ui, sans-serif`;
  const remember = (rect: Rect): void => {
    scope.bounds?.set(shape.id, rect);
  };

  switch (shape.kind) {
    case 'arrow':
    case 'arrow-curve': {
      if (!b) return;
      // У изогнутой вторая опора — конец, третья — изгиб.
      const bend = shape.kind === 'arrow-curve' ? (c ?? null) : null;
      cometArrow(ctx, a, b, bend, shape.width, color);
      if (shape.text) {
        const card = measureCard(ctx, shape.text, font, { x: 0, y: 0 }, style);
        const w = card.rect.right;
        const h = card.rect.bottom;
        // Подпись у хвоста — под ним, если стрелка идёт вверх, и над ним,
        // если вниз: так она не ложится на саму стрелку и не уходит за край.
        const below = b.y <= a.y;
        const x = Math.max(4, Math.min(scope.width - w - 4, a.x - w / 2));
        const placed = measureCard(ctx, shape.text, font, { x, y: below ? a.y + 12 : a.y - h - 12 }, style);
        glassCard(ctx, placed, color, style, font);
        remember(placed.rect);
      } else {
        remember({ left: Math.min(a.x, b.x) - 6, top: Math.min(a.y, b.y) - 6, right: Math.max(a.x, b.x) + 6, bottom: Math.max(a.y, b.y) + 6 });
      }
      return;
    }

    case 'arrow-up':
    case 'arrow-down': {
      // Остриё — у свечи; длина — до второй опоры, не меньше 36 пикселей.
      const up = shape.kind === 'arrow-up';
      const length = Math.max(36, b ? Math.abs(b.y - a.y) : 44);
      const tail = { x: a.x, y: a.y + (up ? length : -length) };
      // Вторая опора задаёт только длину — стоит на хвосте, а не где её отпустили.
      scope.anchors?.set(shape.id, [a, tail]);
      cometArrow(ctx, tail, a, null, Math.max(shape.width, 2) + 1, color);
      if (shape.text) {
        const probe = measureCard(ctx, shape.text, font, { x: 0, y: 0 }, style);
        const w = probe.rect.right;
        const h = probe.rect.bottom;
        const placed = measureCard(ctx, shape.text, font, { x: tail.x - w / 2, y: up ? tail.y + 6 : tail.y - h - 6 }, style);
        glassCard(ctx, placed, color, style, font);
        remember(placed.rect);
      } else {
        remember({ left: a.x - 12, top: Math.min(a.y, tail.y), right: a.x + 12, bottom: Math.max(a.y, tail.y) });
      }
      return;
    }

    case 'note': {
      // Карточка над точкой; хвостик — мягкой кривой к булавке.
      const probe = measureCard(ctx, shape.text ?? '', font, { x: 0, y: 0 }, style);
      const h = probe.rect.bottom;
      const card = measureCard(ctx, shape.text ?? '', font, { x: a.x - 20, y: a.y - h - 16 }, style);
      glassCard(ctx, card, color, style, font, 'Заметка');
      const baseY = Math.round(card.rect.bottom) + 0.5;
      const from = Math.max(card.rect.left + 14, Math.min(card.rect.right - 24, a.x - 6));
      ctx.save();
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.moveTo(from, baseY - 1);
      ctx.quadraticCurveTo(from + 3, baseY + 6, a.x, a.y - 6);
      ctx.quadraticCurveTo(from + 10, baseY + 4, from + 14, baseY - 1);
      ctx.closePath();
      ctx.fillStyle = style.background ? tint(style.backgroundColor, style.backgroundOpacity) : 'rgba(12, 16, 31, 0.96)';
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(from, baseY);
      ctx.quadraticCurveTo(from + 3, baseY + 6, a.x, a.y - 6);
      ctx.quadraticCurveTo(from + 10, baseY + 4, from + 14, baseY);
      ctx.strokeStyle = style.border ? tint(color, 0.6) : 'rgba(255, 255, 255, 0.08)';
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.restore();
      pin(ctx, a, color);
      remember({ ...card.rect, bottom: a.y + 6 });
      return;
    }

    case 'callout': {
      if (!b) return;
      const card = measureCard(ctx, shape.text ?? '', font, b, style);
      // Соединитель — мягкая кривая от булавки к ближнему краю карточки.
      const target = { x: a.x < b.x ? card.rect.left : card.rect.right, y: (card.rect.top + card.rect.bottom) / 2 };
      const bend = { x: (a.x + target.x) / 2, y: a.y };
      ctx.save();
      ctx.setLineDash(shape.style === 'dashed' ? [6, 4] : shape.style === 'dotted' ? [1, 3] : []);
      const wire = ctx.createLinearGradient(a.x, a.y, target.x, target.y);
      wire.addColorStop(0, tint(color, 1));
      wire.addColorStop(1, tint(color, 0.45));
      ctx.strokeStyle = wire;
      ctx.lineWidth = Math.max(1.3, shape.width * 0.8);
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.quadraticCurveTo(bend.x, bend.y, target.x, target.y);
      ctx.stroke();
      ctx.restore();
      glassCard(ctx, card, color, style, font, 'Сноска');
      // Разъём на краю карточки — там, куда пришёл соединитель.
      ctx.save();
      ctx.beginPath();
      ctx.arc(target.x, target.y, 3, 0, Math.PI * 2);
      ctx.fillStyle = '#070B16';
      ctx.fill();
      ctx.lineWidth = 1.4;
      ctx.strokeStyle = tint(color, 0.9);
      ctx.stroke();
      ctx.restore();
      pin(ctx, a, color);
      remember(card.rect);
      return;
    }

    case 'price-label': {
      // Ярлык: пилюля с носиком, указывающим на цену.
      const text = priceWords(shape.points[0]?.price);
      ctx.save();
      ctx.setLineDash([]);
      ctx.font = '700 12px Onest, system-ui, sans-serif';
      const width = ctx.measureText(text).width + 22;
      const height = 22;
      const left = a.x + 10;
      const top = a.y - height / 2;
      ctx.beginPath();
      ctx.moveTo(a.x + 1, a.y);
      ctx.lineTo(left, top);
      ctx.lineTo(left + width - height / 2, top);
      ctx.arc(left + width - height / 2, a.y, height / 2, -Math.PI / 2, Math.PI / 2);
      ctx.lineTo(left, top + height);
      ctx.closePath();
      const fill = ctx.createLinearGradient(left, top, left + width, top);
      fill.addColorStop(0, tint(color, 0.95));
      fill.addColorStop(1, tint(color, 0.7));
      ctx.shadowColor = tint(color, 0.5);
      ctx.shadowBlur = 10;
      ctx.fillStyle = fill;
      ctx.fill();
      ctx.shadowBlur = 0;
      ctx.fillStyle = '#060B15';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, left + 6, a.y + 0.5);
      ctx.restore();
      remember({ left: a.x, top, right: left + width, bottom: top + height });
      return;
    }

    case 'flag': {
      // Древко и полотнище с надписью: полотнище растёт по тексту.
      const text = (shape.text ?? '').trim();
      ctx.save();
      ctx.setLineDash([]);
      ctx.font = font;
      const lines = text ? wrapText(text, style, (line) => ctx.measureText(line).width) : [];
      const lineHeight = Math.round(style.fontSize * 1.4);
      const clothW = text ? Math.max(...lines.map((line) => ctx.measureText(line).width)) + 30 : 36;
      const clothH = text ? lines.length * lineHeight + 10 : 22;
      const poleTop = a.y - 26 - clothH;
      const x0 = a.x;
      const wave = Math.min(6, clothH * 0.2);

      // Древко — тонкое, с бликом сверху.
      const pole = ctx.createLinearGradient(x0, a.y, x0, poleTop);
      pole.addColorStop(0, tint(color, 0.5));
      pole.addColorStop(1, lighter(color, 0.55, 1));
      ctx.strokeStyle = pole;
      ctx.lineWidth = 1.6;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(x0, a.y);
      ctx.lineTo(x0, poleTop - 3);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(x0, poleTop - 4, 2.2, 0, Math.PI * 2);
      ctx.fillStyle = lighter(color, 0.6, 1);
      ctx.fill();

      // Полотнище — волной, с мягкой тенью и переливом.
      const top = poleTop;
      const right = x0 + clothW;
      ctx.beginPath();
      ctx.moveTo(x0, top);
      ctx.bezierCurveTo(x0 + clothW * 0.33, top - wave, x0 + clothW * 0.66, top + wave, right, top);
      ctx.lineTo(right, top + clothH);
      ctx.bezierCurveTo(x0 + clothW * 0.66, top + clothH + wave, x0 + clothW * 0.33, top + clothH - wave, x0, top + clothH);
      ctx.closePath();
      const cloth = ctx.createLinearGradient(x0, top, right, top + clothH);
      cloth.addColorStop(0, tint(color, 1));
      cloth.addColorStop(1, lighter(color, 0.3, 0.92));
      ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
      ctx.shadowBlur = 14;
      ctx.shadowOffsetY = 4;
      ctx.fillStyle = cloth;
      ctx.fill();
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
      ctx.shadowOffsetY = 0;
      // Складка — светлая полоса по волне.
      ctx.beginPath();
      ctx.moveTo(x0 + 2, top + 2);
      ctx.bezierCurveTo(x0 + clothW * 0.33, top - wave + 2, x0 + clothW * 0.66, top + wave + 2, right - 2, top + 2);
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.35)';
      ctx.lineWidth = 1;
      ctx.stroke();

      if (text) {
        ctx.fillStyle = isLight(color) ? '#0B1020' : '#FFFFFF';
        ctx.textBaseline = 'middle';
        lines.forEach((line, i) => {
          ctx.fillText(line, x0 + 14, top + 5 + lineHeight * (i + 0.5));
        });
      }
      ctx.restore();
      pin(ctx, a, color);
      remember({ left: a.x - 7, top: top - wave - 6, right: right + 4, bottom: a.y + 7 });
      return;
    }
  }
}
